import { PositionOnMapOutline24Icon } from "@vygruppen/spor-icon-react";
import { VehicleMapMarkers } from "features/CenterContent/RoleContent/VehicleMap/VehicleMapMarkers";
import FitToBounds from "features/CenterContent/shared/Map/FitToBounds";
import { TooltipArrowColor } from "features/CenterContent/shared/Map/LeafletTooltip";
import { BaseGpsData } from "features/CenterContent/shared/types";
import { latLngBounds, LatLngLiteral, Map } from "leaflet";
import { FC, useEffect, useRef } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { TextLink } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { formatDayDateString } from "shared/utils/dateTimeUtils";
import styled from "styled-components";

type VehicleMapContentProps = {
  gpsData: BaseGpsData[];
};

// TooltipArrowColor is added here since you are unable to add it at the tooltip-level, it must be added globally
const MapWrapper = styled.div`
  .leaflet-tooltip-top:before {
    border-top-color: ${TooltipArrowColor};
  }

  overflow: hidden;
  position: relative;
  display: grid;
  grid-template-rows: 1fr auto;
  height: 100%;
  width: 100%;
  border-radius: 6px;
`;

const GrayscaleTileLayer = styled(TileLayer)`
  -webkit-filter: grayscale(100%);
  filter: grayscale(100%);
`;

const MapInfo = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 12px;
`;

export const GPSContent: FC<VehicleMapContentProps> = ({ gpsData }) => {
  const coords: LatLngLiteral[] = gpsData.map((data) => ({
    lat: data.gps.latitude,
    lng: data.gps.longitude,
  }));

  const bounds = latLngBounds(coords);

  /**
   * Refreshes the map whenever its container resizes
   */
  const mapRef = useRef<Map | null>(null);
  const resizeTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = window.setTimeout(() => {
        if (mapRef.current) {
          // Invalidate size first to tell Leaflet the container changed
          mapRef.current.invalidateSize();

          if (bounds.isValid()) {
            mapRef.current.fitBounds(bounds, {
              padding: [30, 35],
              maxZoom: 16,
            });
          }
        }
      }, 150);
    };

    // Window resize
    window.addEventListener("resize", handleResize);

    // ResizeObserver for the map's actual DOM container
    let resizeObserver: ResizeObserver | null = null;

    const setupResizeObserver = () => {
      if (mapRef.current && window.ResizeObserver) {
        const mapContainer = mapRef.current.getContainer();
        if (mapContainer) {
          resizeObserver = new ResizeObserver(handleResize);
          resizeObserver.observe(mapContainer);
        }
      }
    };

    // Set up observer after a brief delay to ensure map is fully initialized
    const observerTimeout = setTimeout(setupResizeObserver, 100);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(observerTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [bounds]);
  return (
    <MapWrapper>
      <MapContainer
        ref={mapRef}
        attributionControl={false}
        scrollWheelZoom={false}
        bounds={bounds}
        boundsOptions={{ padding: [30, 35], maxZoom: 16 }}
        style={{
          height: "100%",
          width: "100%",
        }}
      >
        <GrayscaleTileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <VehicleMapMarkers gpsData={gpsData} />
        <FitToBounds bounds={bounds} />
      </MapContainer>

      <MapInfo>
        {gpsData.map((marker) => (
          <div
            key={marker.id}
            style={{ display: "flex", gap: "6px", alignItems: "center" }}
          >
            <TextLink
              variant="primary"
              href={`https://maps.google.com/maps?t=k&q=loc:${marker.gps.latitude},${marker.gps.longitude}`}
              target="_blank"
              rel="noopener noreferer"
            >
              <PositionOnMapOutline24Icon style={{ marginRight: "6px" }} />
              {marker.id}
            </TextLink>
            <Text secondary>({formatDayDateString(marker.timestamp)})</Text>
          </div>
        ))}
      </MapInfo>
    </MapWrapper>
  );
};
